import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';
import { Flex, Image, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import cloudUpload from '../../../../cloud_upload.png';
import { CypressIds } from '../../../../constants/cypressIds';
import { createErrorToast } from '../../../../redux/modules/app';
import colors from '../../../../styles/theme/foundation/colors';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const FileDropzone = ({ onFilesAdded, maxFiles = 1, ...props }) => {
  const dispatch = useDispatch();

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      const tooManyFiles = fileRejections.some(rejection => rejection.errors.some(err => err.code === 'too-many-files'));
      const fileTooLarge = fileRejections.some(rejection => rejection.errors.some(err => err.code === 'file-too-large'));
      const fileInvalid = fileRejections.some(rejection => rejection.errors.some(err => err.code === 'file-invalid-type'));

      if (tooManyFiles) {
        dispatch(createErrorToast({
          title: 'Please upload only one file',
          closeAllToasts: false
        }));

        return;
      }

      if (fileTooLarge) {
        dispatch(createErrorToast({
          title: 'File too large',
          description: 'File is larger than 50MB',
          closeAllToasts: false
        }));

        return;
      }

      if (fileInvalid) {
        dispatch(createErrorToast({
          title: 'Unsupported file type',
          description: 'This file is not a supported file type',
          closeAllToasts: false
        }));

        return;
      }
    }

    // acceptedFiles is an array of File objects
    onFilesAdded(acceptedFiles);
  }, [onFilesAdded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: maxFiles > 1,
    maxFiles,
    maxSize: MAX_FILE_SIZE,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'text/plain': ['.txt']
    }
  });

  return (
    <Flex
      {...getRootProps()}
      alignItems="center"
      backgroundColor="greyScale.100"
      border={`1px dashed ${colors.primaryScale['100']}`}
      borderRadius="lg"
      cursor="pointer"
      flexDir="column"
      h="300px"
      justifyContent="center"
      padding={4}
      textAlign="center"
      w="400px"
      {...props}
    >
      <Image mx="auto" src={cloudUpload} />
      <input data-cy={CypressIds.certUploadDocFileButton} {...getInputProps()} />
      {isDragActive ? (
        <Text mb={2} textStyle="bodyXxl500">Drop the files here…</Text>
      ) : (
        <Text mb={2} textStyle="bodyXxl500">Drag & drop files here, or browse</Text>
      )}
      <Text color="greyScale.500" textStyle="bodyMd">Accepted formats: PDF, JPG, PNG, GIF, WEBP, CSV, XLS, XLSX, DOC, DOCX, TXT</Text>
      <Text color="greyScale.500" textStyle="bodyMd">Max 50 MB per file</Text>
      <Text color="greyScale.500" textStyle="bodyMd">Max 100 MB total</Text>
    </Flex>
  );
};

FileDropzone.propTypes = {
  onFilesAdded: PropTypes.func.isRequired,
  maxFiles: PropTypes.number
};

export default FileDropzone;
